'use client';

import axios, { type AxiosInstance } from 'axios';

import type {
  AttachCard3DSResponseData,
  CreateManyReceiversResponse,
  DonationBaseResponce,
  TAccumulationSummary,
  TAttachReceiverCardData,
  TCardInfo,
  TDonationReceiverCreateData,
  TDonationReceiverInfo,
  TPayoutResponse,
  TTransactionDatasResponse,
  TTransactionsPayoutResponse,
} from '@/types/donations';

class DonationsApiClient {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: '/api/donations',
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  /**
   * Создание получателя донатов (CloudTips receiver)
   */
  async createDonationReceiver(receiver: TDonationReceiverCreateData) {
    try {
      const response = await this.api.post<CreateManyReceiversResponse>('/receiver', receiver);
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Отправка SMS для привязки получателя к заведению
   */
  async sendAttachSms(params: { donationUserId: string }) {
    const response = await this.api.post<DonationBaseResponce>('/attach-receiver/send-sms', params);
    return response.data;
  }

  /**
   * Подтверждение привязки получателя по SMS
   */
  async confirmReceiverAttach(params: { donationUserId: string; smsCode: string }) {
    const response = await this.api.post<DonationBaseResponce>('/attach-receiver/confirm', params);
    return response.data;
  }

  /**
   * Подключение карты получателя
   */
  async attachReceiverCard(params: Omit<TAttachReceiverCardData, 'ipSource'>) {
    const response = await this.api.post<DonationBaseResponce<AttachCard3DSResponseData>>(
      '/attach-receiver/attach-card',
      params,
    );
    return response.data;
  }

  /**
   * Загрузка данных получателя
   */
  async fetchReceiverInfo(donationUserId: string) {
    const response = await this.api.get<DonationBaseResponce<TDonationReceiverInfo>>(`/receiver`, {
      params: {
        donationUserId,
      },
    });
    return response.data;
  }
  async fetchReceiverPaymentLink(donationUserId: string) {
    const response = await this.fetchReceiverInfo(donationUserId);
    return response.data?.layouts[0].paymentLink;
  }

  /**
   * Получение publicId для криптограммы карты
   */
  async fetchCardPublicId(donationUserId: string): Promise<string> {
    const response = await this.api.get<DonationBaseResponce<{ publicId: string }>>(
      '/attach-receiver/public-id',
      { params: { donationUserId } },
    );
    if (!response.data.data?.publicId) throw new Error('Failed to get publicId');
    return response.data.data.publicId;
  }

  /**
   * Загрузка карты получателя
   */
  async fetchReceiverCards(donationUserId: string) {
    const response = await this.api.get<DonationBaseResponce<TCardInfo[]>>(`/receiver/card`, {
      params: {
        donationUserId,
      },
    });
    return response.data;
  }

  /**
   * Удаленые карты получателя
   */
  async disconnectDonations(cardToken: string, donationUserId: string) {
    const response = await this.api.delete<{
      // deleteReceiverSucceed: boolean
      deleteReceiverCardSucceed: boolean;
    }>(`/receiver/card`, {
      data: {
        cardToken,
        donationUserId,
      },
    });
    return response.data;
  }

  async fetchReceiverTransactions(donationUserId: string) {
    const response = await this.api.get<DonationBaseResponce<TTransactionDatasResponse>>(
      `/receiver/transactions`,
      {
        params: {
          donationUserId,
        },
      },
    );
    return response.data;
  }

  /**
   * Получение сводки накоплений
   */
  async fetchAccumulationSummary(donationUserId: string) {
    const response = await this.api.get<DonationBaseResponce<TAccumulationSummary>>(
      '/accumulations',
      { params: { donationUserId } },
    );
    return response.data;
  }

  /**
   * Fetch payout transactions list
   */
  async fetchPayoutTransactions(donationUserId: string) {
    const response = await this.api.get<DonationBaseResponce<TTransactionsPayoutResponse>>(
      '/payout-transactions',
      { params: { donationUserId } },
    );
    return response.data;
  }

  /**
   * Запрос выплаты накоплений
   */
  async requestAccumulationPayout(donationUserId: string) {
    const response = await this.api.post<TPayoutResponse>('/accumulations/payout', {
      donationUserId,
    });
    return response.data;
  }
}

export const donationsApiClient = new DonationsApiClient();
